import React, { useEffect, useState, useRef } from "react";
import { getPartners } from "../../../services/HomePage/PartnerApi";
import { FaChevronLeft, FaChevronRight, FaHandshake, FaStar } from "react-icons/fa";

import fallbackImg from "../../assets/logo/appstore.png";

export default function PartnerSlider() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  const scrollRef = useRef();

  // Build image base URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const BASE_IMG = API_BASE_URL?.replace("/api/", "/images/EmployeesImg/");

  // Fetch partners from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getPartners();
        setPartners(data);
      } catch (err) {
        console.error("Partner API Error:", err);
        setPartners([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto‑scroll effect (right to left)
  useEffect(() => {
    if (partners.length === 0) return;

    const scrollInterval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

        // If near the end, jump back to start smoothly
        if (scrollLeft + clientWidth >= scrollWidth - 5) {
          scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          scrollRef.current.scrollBy({ left: 180, behavior: "smooth" });
        }
      }
    }, 2500);

    return () => clearInterval(scrollInterval);
  }, [partners.length]);

  // Helper to build image URL
  const getImageUrl = (image) => {
    if (!image) return fallbackImg;
    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }
    const cleanImage = image.startsWith("/") ? image.substring(1) : image;
    return `${BASE_IMG}${cleanImage}`;
  };

  const handleImageError = (partnerId) => {
    setImageErrors((prev) => ({ ...prev, [partnerId]: true }));
  };

  const scrollLeft = () => {
    scrollRef.current.scrollBy({ left: -250, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current.scrollBy({ left: 250, behavior: "smooth" });
  };

  // Loading state
  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
        <div className="text-center py-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-4">
            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse"></span>
            <span className="text-xs font-bold tracking-wide">LOADING PARTNERS</span>
          </div>
          <div className="animate-pulse">
            <div className="flex justify-center gap-6 flex-wrap">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="text-center">
                  <div className="w-[80px] h-[80px] bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-20 mx-auto mt-2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No data
  if (partners.length === 0) return null;

  // Main render
  return (
    <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
      
      {/* Header */}
      <div className="text-center mb-8">
         
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
            Healthcare
          </span>{" "}
          <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
            Partners
          </span>
        </h2>
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          Trusted healthcare providers across India
        </p>
    
      </div>

      <div className="relative group">
        {/* Navigation arrows */}
        <button
          onClick={scrollLeft}
          className="absolute -left-3 md:-left-4 top-1/2 -translate-y-1/2 z-20 
          bg-white hover:bg-red-500 text-red-500 hover:text-white 
          shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
          opacity-0 group-hover:opacity-100 focus:outline-none"
        >
          <FaChevronLeft size={14} />
        </button>

        <button
          onClick={scrollRight}
          className="absolute -right-3 md:-right-4 top-1/2 -translate-y-1/2 z-20 
          bg-white hover:bg-red-500 text-red-500 hover:text-white 
          shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
          opacity-0 group-hover:opacity-100 focus:outline-none"
        >
          <FaChevronRight size={14} />
        </button>

        {/* ✅ The key fix: added "justify-center" to center the items */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto scroll-smooth hide-scrollbar justify-center"
          style={{
            gap: "24px",
            padding: "10px 20px",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {partners.map((partner, index) => {
            const partnerId = partner?.id || index;
            const imageUrl = getImageUrl(partner?.image || partner?.Image);
            const hasError = imageErrors[partnerId];
            const partnerName = partner?.name || partner?.Name || partner?.company_name || `Partner ${index + 1}`;

            return (
              <div
                key={partnerId}
                className="group/brand flex-shrink-0 text-center cursor-pointer transform transition-all duration-300 hover:-translate-y-1"
                style={{ width: "100px" }}
              >
                {/* Circular logo */}
                <div className="relative w-[80px] h-[80px] md:w-[90px] md:h-[90px] rounded-full overflow-hidden 
                bg-gradient-to-br from-gray-100 to-gray-200 shadow-md hover:shadow-xl 
                transition-all duration-300 group-hover/brand:shadow-lg mx-auto">
                  
                  {!hasError && (partner?.image || partner?.Image) ? (
                    <img
                      src={imageUrl}
                      alt={partnerName}
                      className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover/brand:scale-110"
                      onError={() => handleImageError(partnerId)}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-100 to-red-200">
                      <img src={fallbackImg} alt="fallback" className="w-10 h-10 object-contain" />
                    </div>
                  )}
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-red-600/50 via-transparent to-transparent 
                  opacity-0 group-hover/brand:opacity-100 transition-opacity duration-300 rounded-full" />
                  
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/brand:opacity-100 transition-all duration-300">
                    <span className="text-white text-[10px] font-bold bg-black/50 px-2 py-1 rounded-full">
                      View
                    </span>
                  </div>
                </div>
                
                {/* Partner name */}
                <p className="text-xs font-semibold text-gray-700 group-hover/brand:text-red-600 
                transition-colors duration-300 mt-2 max-w-[100px] truncate">
                  {partnerName}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trust badges */}
      <div className="mt-8 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-[10px] font-bold">✓</span>
            </div>
            <span>100+ Trusted Partners</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-[10px] font-bold">⚡</span>
            </div>
            <span>Pan India Service</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 text-[10px] font-bold">🏆</span>
            </div>
            <span>Quality Assured</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-[10px] font-bold">❤️</span>
            </div>
            <span>24/7 Support</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      `}</style>
    </div>
  );
}